import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { isMobileOnly, isMobile } from 'react-device-detect';
import { Box, Container, IconButton, Tooltip, Popover } from '@mui/material';
import { FullscreenSharp, FullscreenExitSharp, RemoveCircleOutline, AddCircleOutline, HelpOutline, GridOn, GridOff, CameraAltOutlined, CompareArrows, Thermostat } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { checkFile, ConditionalWrapper } from "../Utilities";

function usePopoverHoverClose(iconRef, popoverRef, onClose) {
    useEffect(() => {
        const onLeave = (event) => {
            if (!iconRef.current ||
                !popoverRef.current ||
                iconRef.current.contains(event.target) ||
                popoverRef.current.contains(event.target)) {
                return
            }
            onClose()
        }
        document.addEventListener("mousemove", onLeave)
        return () => document.removeEventListener("mousemove", onLeave)
    }, [iconRef, popoverRef, onClose])
}

const popoverContentSx = {
    p: 0.5,
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    textAlign: 'center'
}

const CustomTooltip = ({ children, title }) => (
    <Tooltip title={title} arrow componentsProps={{ popper: { disablePortal: true } }}>
        {children}
    </Tooltip>
)

export default function Controlbar(props) {

    const { t } = useTranslation()
    const { zoom, zoomUtils, fullscreenHandle, disabled, loading, hasOverview, showOverview, handleTakeSnapshot, handleOverviewClick, imageSource, disableOverview, hasCompare, showCompare, handleCompareClick, hasLastModified = true, hasZoom = true, hasWeather, weatherData } = props
    const { LAYOUT: { PREVENT_ZOOM } } = window.conf

    const [lastModified, setLastModified] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openLastModifiedTooltip, setOpenLastModifiedTooltip] = useState(false);
    const [anchorWeatherEl, setAnchorWeatherEl] = useState(null);
    const [openWeatherTooltip, setOpenWeatherTooltip] = useState(false);

    const refLastModifiedIcon = useRef(null);
    const refLastModifiedPopover = useRef(null);
    const refWeatherIcon = useRef(null);
    const refWeatherPopover = useRef(null);
    const openLastModifiedPopover = Boolean(anchorEl);
    const openWeatherPopover = Boolean(anchorWeatherEl);
    const idLastModifiedPopover = openLastModifiedPopover ? "last-modified-popover" : undefined;
    const idWeatherPopover = openWeatherPopover ? "weather-popover" : undefined;
    const disableZoomOut = (disabled || (zoom === 1 && showOverview)) || (!hasOverview && zoom === 1) || (disableOverview && zoom === 1)
    const disabledByConfig = PREVENT_ZOOM || !hasZoom
    const disabledByOverview = disabled || showOverview
    const disabledByCompare = disabled || showCompare
    const disableCompareCaption = isMobile

    // if overview grid is visible than toggle (hide) overview grid, else zoom in
    function onClickOnZoomIn() {
        if (showOverview) {
            handleOverviewClick && handleOverviewClick()
        } else {
            zoomUtils.zoomIn()
        }
    }

    // if current zoom is >100% zoom out, else toggle (open) overview grid
    function onClickOnZoomOut() {
        if (zoom > 1) {
            zoomUtils.zoomOut()
        } else {
            handleOverviewClick && handleOverviewClick()
        }
    }

    function onClickLastModifiedButton(event) {
        event.stopPropagation();
        setOpenLastModifiedTooltip(false);
        setAnchorEl(event.currentTarget);
    };

    function onCloseLastModifiedPopover() {
        setAnchorEl(null);
    };

    function onClickWeatherButton(event) {
        event.stopPropagation();
        setOpenWeatherTooltip(false);
        setAnchorWeatherEl(event.currentTarget);
    };

    function onCloseWeatherPopover() {
        setAnchorWeatherEl(null);
    };
    
    useEffect(() => {
        let cancelled = false
        const checkLastModified = async() => {
            const { exists, contentType, lastModified } = await checkFile(imageSource);
            if (!cancelled && exists && contentType === "image" && lastModified) {
                setLastModified(lastModified);
            }
        }
        checkLastModified();
        return () => { cancelled = true }
    }, [imageSource]);

    usePopoverHoverClose(refLastModifiedIcon, refLastModifiedPopover, onCloseLastModifiedPopover);
    usePopoverHoverClose(refWeatherIcon, refWeatherPopover, onCloseWeatherPopover);

    return (
        <Container className={isMobileOnly || loading ? "Controlbar-mobileFooter" : "Controlbar-footer"}>
            <Box className="Controlbar-wrapper" sx={{ justifyContent: "center" }}>
            
               <Box sx={{ display: 'flex', width: '100%' }}>

                    {/* placeholder for spacing */}

                    <Box className='control-item'></Box>

                    {/* zoom */}

                    {!disabledByConfig && (
                        <Box className='control-item'>
                            <Box>
                                <ConditionalWrapper
                                    condition={!disableZoomOut && !showCompare}
                                    wrapper={children => <CustomTooltip title={t("actions.zoom_out")}>{children}</CustomTooltip>}
                                >
                                    <Box sx={{ display: 'inline-block' }}>
                                        <IconButton
                                            className="Controlbar-iconbutton"
                                            aria-label="zoom out"
                                            onClick={onClickOnZoomOut}
                                            disabled={disableZoomOut || showCompare}
                                        >
                                            {disableZoomOut || showCompare
                                            ? <RemoveCircleOutline sx={{ color: "#606060", fontSize: "1.5rem" }} />
                                            : <RemoveCircleOutline sx={{ color: "white", fontSize: "1.5rem" }} />}
                                        </IconButton>
                                    </Box>
                                </ConditionalWrapper>

                                <Box
                                    component="span"
                                    className="Controlbar-label"
                                    sx={{ color: disabled || showCompare ? "#606060" : "white" }}
                                >
                                    {showOverview ? 10 : parseInt(zoom * 100)}%
                                </Box>

                                <ConditionalWrapper
                                    condition={!(zoom === 8 || disabled || showCompare)}
                                    wrapper={children => <CustomTooltip title={t("actions.zoom_in")}>{children}</CustomTooltip>}
                                >
                                    <Box sx={{ display: 'inline-block' }}>
                                        <IconButton
                                            className="Controlbar-iconbutton"
                                            aria-label="zoom in"
                                            onClick={onClickOnZoomIn}
                                            disabled={zoom === 8 || disabled || showCompare}
                                        >
                                            {zoom === 8 || disabled || showCompare
                                            ? <AddCircleOutline sx={{ color: '#606060', fontSize: "1.5rem" }} />
                                            : <AddCircleOutline sx={{ color: 'white', fontSize: "1.5rem" }} />}
                                        </IconButton>
                                    </Box>
                                </ConditionalWrapper>
                            </Box>
                        </Box>
                    )}

                    {/* right aligned controls */}

                    <Box className='control-item' sx={{ textAlign: 'right' }}>

                        {/* image compare */}

                        {hasCompare && (
                            <Box component="span">
                                <IconButton
                                    className="Controlbar-iconbutton"
                                    aria-label="before-after"
                                    onClick={handleCompareClick}
                                    disabled={disabled || showOverview}
                                >
                                    <Box component="span" sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <CompareArrows sx={{ color: disabled || showOverview ? '#606060' : 'white', fontSize: "1.2rem" }} />
                                        {showCompare && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    position: 'absolute',
                                                    width: '90%',
                                                    height: '1px',
                                                    backgroundColor: 'white',
                                                    transform: 'rotate(45deg)',
                                                    borderRadius: '1px'
                                                }}
                                            />
                                        )}
                                    </Box>
                                </IconButton>

                                {!disableCompareCaption && (
                                    <Box
                                        component="span"
                                        className="Controlbar-label"
                                        sx={{
                                            color: disabled || showOverview ? '#606060' : 'white',
                                            cursor: disabled || showOverview ? 'default' : 'pointer',
                                            mr: 1
                                        }}
                                        onClick={disabled || showOverview ? undefined : handleCompareClick}
                                    >
                                        {t("actions.before_after")}
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* overview */}

                        {hasOverview && (
                            <ConditionalWrapper
                                condition={!disableOverview && !disabledByCompare}
                                wrapper={children => <CustomTooltip title={t("actions.overview")}>{children}</CustomTooltip>}
                            >
                                <Box component="span">
                                    <IconButton
                                        className="Controlbar-iconbutton"
                                        aria-label="overview"
                                        onClick={handleOverviewClick}
                                        disabled={disableOverview || disabledByCompare}
                                    >
                                        {disableOverview || disabledByCompare
                                        ? showOverview
                                            ? <GridOff sx={{ color: '#606060', fontSize: "1rem" }} />
                                            : <GridOn sx={{ color: '#606060', fontSize: "1rem" }} />
                                        : showOverview
                                            ? <GridOff sx={{ color: 'white', fontSize: "1rem" }} />
                                            : <GridOn sx={{ color: 'white', fontSize: "1rem" }} />}
                                    </IconButton>
                                </Box>
                            </ConditionalWrapper>
                        )}

                        {/* weather */}

                        {hasWeather && (
                            <ConditionalWrapper
                                condition={!disabledByOverview && !disabledByCompare && weatherData && weatherData.length > 0}
                                wrapper={children =>
                                    <Tooltip
                                        open={openWeatherTooltip}
                                        onClose={() => setOpenWeatherTooltip(false)}
                                        onOpen={() => !openWeatherPopover && setOpenWeatherTooltip(true)}
                                        title={t("weather.weather")}
                                        arrow
                                        componentsProps={{ popper: { disablePortal: true } }}
                                    >
                                        {children}
                                    </Tooltip>
                                }
                            >
                                <Box component="span">
                                    <IconButton
                                        ref={refWeatherIcon}
                                        className="Controlbar-iconbutton"
                                        aria-label="weather"
                                        disabled={disabledByOverview || disabledByCompare || !weatherData || weatherData.length === 0}
                                        onClick={onClickWeatherButton}
                                        sx={{ ...(openWeatherPopover && { zIndex: 99999 }) }}
                                    >
                                        {disabledByOverview || disabledByCompare || !weatherData || weatherData.length === 0
                                        ? <Thermostat sx={{ color: "#606060", fontSize: "1.2rem" }} />
                                        : <Thermostat sx={{ color: "white", fontSize: "1.2rem" }} />}
                                    </IconButton>

                                    <Popover
                                        id={idWeatherPopover}
                                        open={openWeatherPopover}
                                        disablePortal
                                        anchorEl={anchorWeatherEl}
                                        onClose={onCloseWeatherPopover}
                                        onMouseLeave={onCloseWeatherPopover}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                    >
                                        <Box
                                            ref={refWeatherPopover}
                                            sx={{ ...popoverContentSx, maxWidth: '230px' }}
                                        >
                                            {weatherData.map((value, index) => (
                                                <Box
                                                    key={value.id ?? index}
                                                    sx={{ mb: index < weatherData.length - 1 ? 0.5 : 0 }}
                                                >
                                                    {value.description}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Popover>
                                </Box>
                            </ConditionalWrapper>
                        )}

                        {/* last modified */}

                        {hasLastModified && (
                            <ConditionalWrapper
                                condition={!disabledByOverview && !disabledByCompare && lastModified}
                                wrapper={children =>
                                    <Tooltip
                                        open={openLastModifiedTooltip}
                                        onClose={() => setOpenLastModifiedTooltip(false)}
                                        onOpen={() => !openLastModifiedPopover && setOpenLastModifiedTooltip(true)}
                                        title={t("msg.last_update")}
                                        arrow
                                        componentsProps={{ popper: { disablePortal: true } }}
                                    >
                                        {children}
                                    </Tooltip>
                                }
                            >
                                <Box component="span">
                                    <IconButton
                                        ref={refLastModifiedIcon}
                                        className="Controlbar-iconbutton"
                                        aria-label="help-outline"
                                        disabled={disabledByOverview || disabledByCompare || !lastModified}
                                        onClick={onClickLastModifiedButton}
                                        sx={{ ...(openLastModifiedPopover && { zIndex: 99999 }) }}
                                    >

                                        {disabledByOverview || disabledByCompare || !lastModified
                                        ? <HelpOutline sx={{ color: "#606060", fontSize: "1.1rem" }} />
                                        : <HelpOutline sx={{ color: "white", fontSize: "1.1rem" }} />}

                                    </IconButton>

                                    <Popover
                                        id={idLastModifiedPopover}
                                        open={openLastModifiedPopover}
                                        disablePortal
                                        anchorEl={anchorEl}
                                        onClose={onCloseLastModifiedPopover}
                                        onMouseLeave={onCloseLastModifiedPopover}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                    >
                                        <Box
                                            ref={refLastModifiedPopover}
                                            sx={{ ...popoverContentSx, maxWidth: '165px' }}
                                        >
                                            {`${t("msg.last_update")}: ${dayjs(lastModified).format(t("date.format.full_date_time"))}`}
                                        </Box>
                                    </Popover>
                                </Box>
                            </ConditionalWrapper>
                        )}

                        {/* snapshot */}

                        <ConditionalWrapper
                            condition={!disabledByOverview && !disabledByCompare}
                            wrapper={children => <CustomTooltip title={t("actions.screenshot")}>{children}</CustomTooltip>}
                        >
                            <Box component="span">
                                <IconButton
                                    className="Controlbar-iconbutton"
                                    aria-label="snapshot"
                                    onClick={handleTakeSnapshot}
                                    disabled={disabledByOverview || disabledByCompare}
                                >
                                    {disabledByOverview || disabledByCompare
                                    ? <CameraAltOutlined sx={{ color: '#606060', fontSize: "1.2rem", mt: "1px" }} />
                                    : <CameraAltOutlined sx={{ color: 'white', fontSize: "1.2rem", mt: "1px" }} />}
                                </IconButton>
                            </Box>
                        </ConditionalWrapper>

                        {/* fullscreen */}

                        <ConditionalWrapper
                            condition={!disabledByOverview}
                            wrapper={children => <CustomTooltip title={t("actions.fullscreen")}>{children}</CustomTooltip>}
                        >
                            <Box component="span">
                                <IconButton
                                    className="Controlbar-iconbutton"
                                    aria-label="fullscreen"
                                    onClick={fullscreenHandle.active ? fullscreenHandle.exit : fullscreenHandle.enter}
                                    disabled={disabledByOverview}
                                >
                                    {disabledByOverview
                                    ? fullscreenHandle.active
                                        ? <FullscreenExitSharp sx={{ color: '#606060', fontSize: "1.5rem", mr: "5px" }} />
                                        : <FullscreenSharp sx={{ color: '#606060', fontSize: "1.5rem", mr: "5px" }} />
                                    : fullscreenHandle.active
                                        ? <FullscreenExitSharp sx={{ color: 'white', fontSize: "1.5rem", mr: "5px" }} />
                                        : <FullscreenSharp sx={{ color: 'white', fontSize: "1.5rem", mr: "5px" }} />}
                                </IconButton>
                            </Box>
                        </ConditionalWrapper>
                
                    </Box>
                </Box>
            </Box>
        </Container>
    )
}